import { useEffect } from 'react';
import { RotateCcw, AlertTriangle, Home } from 'lucide-react';
import useStore from '@/store';
import { logger } from '@/utils/logger';
import { ErrorBoundaryProps } from '@/types/components';

/**
 * Minimal store shape this fallback depends on. Typed locally so we can drop
 * the `any` selector while keeping the exact same `setCurrentScreen` usage.
 */
interface ErrorBoundaryStoreSlice {
  setCurrentScreen: (screen: string) => void;
}

/**
 * Categorize an error into a user-facing heading based on substrings in the
 * message. Order matters: camera → network → permission → generic.
 */
function getErrorCategory(error: Error): string {
  const message = error.message;
  if (message.includes('camera') || message.includes('Camera')) {
    return 'Camera Error';
  }
  if (message.includes('network') || message.includes('fetch')) {
    return 'Network Error';
  }
  if (message.includes('permission')) {
    return 'Permission Error';
  }
  return 'Application Error';
}

/**
 * Top-level error fallback rendered by `react-error-boundary`. Surfaces the
 * error category, the raw message/stack for debugging, and three recovery
 * actions: Try Again (reset boundary), Go to Home (navigate + reset), and
 * Reload Page (hard reload).
 */
function ErrorFallback({ error, resetErrorBoundary }: ErrorBoundaryProps) {
  const setCurrentScreen = useStore(
    (state: ErrorBoundaryStoreSlice) => state.setCurrentScreen,
  );

  // Log error details for debugging.
  useEffect(() => {
    logger.error('Error boundary caught error', {
      message: error.message,
      stack: error.stack,
      component: 'ErrorBoundary',
    });
  }, [error]);

  const handleGoHome = (): void => {
    setCurrentScreen('projects');
    resetErrorBoundary();
  };

  const handleRetry = (): void => {
    resetErrorBoundary();
  };

  const handleReload = (): void => {
    window.location.reload();
  };

  return (
    <div role="alert" className="min-h-screen flex items-center justify-center bg-black p-6">
      <div className="max-w-md w-full bg-red-900/50 border-2 border-red-500 rounded-lg p-6 text-center">
        <div className="mb-4">
          <AlertTriangle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-red-400 mb-2">{getErrorCategory(error)}</h2>
          <p className="text-red-300 mb-4">
            We encountered an unexpected error. Don't worry, your data is safe.
          </p>
        </div>

        <div className="bg-red-950/50 border border-red-700 rounded p-3 mb-4 text-left">
          <p className="text-sm text-red-300 mb-2 font-semibold">Error Details:</p>
          <pre
            className="text-xs text-red-400 overflow-auto max-h-64"
            style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
            data-testid="error-details"
          >
            {error && error.message ? `Message: ${error.message}\n` : 'Unknown error occurred\n'}
            {error && error.stack ? `Stack:\n${error.stack}` : ''}
          </pre>
        </div>

        <div className="space-y-3">
          <button
            onClick={handleRetry}
            className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded flex items-center justify-center gap-2 transition-colors"
          >
            <RotateCcw size={16} />
            Try Again
          </button>

          <button
            onClick={handleGoHome}
            className="w-full bg-yellow-400 hover:bg-yellow-300 text-black font-bold py-2 px-4 rounded flex items-center justify-center gap-2 transition-colors"
          >
            <Home size={16} />
            Go to Home
          </button>

          <button
            onClick={handleReload}
            className="w-full bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded transition-colors"
          >
            Reload Page
          </button>
        </div>

        <div className="mt-4 text-xs text-red-400">
          <p>If this problem persists, please contact support.</p>
          <p className="mt-1">Error ID: {Date.now()}</p>
        </div>
      </div>
    </div>
  );
}

export default ErrorFallback;
